import React from 'react';
import * as v0 from '@aidkitorg/types/lib/survey';
import { resolvePortalTimelineStepState, PortalTimelineVariant } from '@aidkitorg/types/lib/timeline';
import { Icon, Timeline, TimelineTone } from '@aidkitorg/component-library';
import { useModularMarkdown } from '../Hooks/ModularMarkdown';
import { MARKDOWN_NO_MARGIN_CLASSES } from '../ApplicantPortalPage';

type Info = Record<string, string | undefined> | undefined;

// Maps each step variant to its marker: an icon (the glyph) + a tone (the
// color). The library owns the styling — these are semantic choices, not styles.
const markerConfig: Record<PortalTimelineVariant, { icon?: React.ReactNode, tone: TimelineTone }> = {
  complete: { icon: <Icon name="CheckIcon" size="sm" />, tone: 'success' },
  in_progress: { icon: <Icon name="ClockIcon" size="sm" />, tone: 'warning' },
  stuck: { icon: <Icon name="ExclamationTriangleIcon" size="sm" />, tone: 'error' },
  not_started: { tone: 'neutral' },
};

// Rendered inside a Timeline.Item so the markdown hook runs once per step.
function StepContent(props: { content: v0.RichText, info: Info, variant: PortalTimelineVariant }) {
  const marked = useModularMarkdown({ content: props.content, info: { ...props.info } });
  return (
    <div className={`text-sm ${MARKDOWN_NO_MARGIN_CLASSES} ${props.variant === 'not_started' ? 'text-slate-400' : 'text-slate-700'}`}>
      {marked}
    </div>
  );
}

export function PortalTimelineComponent(props: { timeline: v0.PortalTimeline, info: Info }) {
  const steps = props.timeline.steps || [];
  return (
    <Timeline>
      {steps.map((step, index) => {
        const state = resolvePortalTimelineStepState(step, props.info || {});
        const variant: PortalTimelineVariant = state?.variant ?? 'not_started';
        const { icon, tone } = markerConfig[variant];
        return (
          <Timeline.Item key={index} icon={icon} tone={tone}>
            <StepContent content={state?.content ?? step.content} info={props.info} variant={variant} />
          </Timeline.Item>
        );
      })}
    </Timeline>
  );
}
